import { useState, useEffect, useCallback } from "react";

import { datadogRum } from "@datadog/browser-rum";

// ----------------------------------------------------------------------

// The initial read and the setter below both swallow storage errors so a
// shimmed or blocked localStorage (extensions, enterprise/DLP agents, privacy
// modes) can no longer crash the app. The crash was previously our only signal,
// so report the swallowed error to RUM at most once per page load to keep these
// environments visible without flooding.
let storageFailureReported = false;

function reportStorageFailure(key: string, error: unknown): void {
  if (storageFailureReported) return;
  storageFailureReported = true;
  datadogRum.addError(error, { feature: "useLocalStorage", storageKey: key });
}

export default function useLocalStorage<ValueType>(
  key: string,
  defaultValue: ValueType
) {
  const [value, setValue] = useState<ValueType>(() => {
    // Reading is best-effort for the same reasons the setter is: a blocked or
    // non-conforming localStorage can make getItem throw, and a corrupt stored
    // value can make JSON.parse throw. Either would crash the initial render, so
    // fall back to defaultValue (what we already return when nothing is stored)
    // and report once.
    try {
      const storedValue = localStorage.getItem(key);
      return storedValue === null ? defaultValue : JSON.parse(storedValue);
    } catch (error) {
      reportStorageFailure(key, error);
      return defaultValue;
    }
  });

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === key) {
        setValue(e.newValue ? JSON.parse(e.newValue) : e.newValue);
      }
    };
    window.addEventListener("storage", listener);

    return () => {
      window.removeEventListener("storage", listener);
    };
  }, [key, defaultValue]);

  const setValueInLocalStorage = useCallback(
    (newValue: ValueType | ((val: ValueType) => ValueType)) => {
      setValue((currentValue) => {
        const resultValue =
          typeof newValue === "function"
            ? (newValue as (val: ValueType) => ValueType)(currentValue)
            : newValue;
        const stringifiedValue = JSON.stringify(resultValue);

        // Persisting and the same-tab broadcast are best-effort. Some browser
        // environments (extensions, enterprise/DLP agents, privacy modes)
        // replace window.localStorage with a non-Storage object, which makes
        // `new StorageEvent({ storageArea })` throw, and setItem can throw when
        // storage is blocked or full. Neither must crash a render; the in-memory
        // state returned below still updates.
        try {
          localStorage.setItem(key, stringifiedValue);

          // Manually dispatch storage event so same-tab listeners update.
          window.dispatchEvent(
            new StorageEvent("storage", {
              key,
              newValue: stringifiedValue,
              oldValue: JSON.stringify(currentValue),
              storageArea: localStorage,
            })
          );
        } catch (error) {
          // storage unavailable / non-conforming: skip persist + broadcast,
          // but report once so these environments stay visible in RUM.
          reportStorageFailure(key, error);
        }

        return resultValue;
      });
    },
    [key]
  );

  return [value, setValueInLocalStorage] as const;
}
